import type { CalculatorField } from 'components/Widget/shared/types';
import type { FeeTreatmentOption, RateType, RateTypeOption, TermUnit, TermUnitOption } from './types';

export const TITLE = 'Loan Calculator';

export const MONTHS_PER_YEAR = 12;

export const RATE_TYPE_OPTIONS: readonly RateTypeOption[] = [
  { key: 'interestRate', label: 'Interest rate' },
  { key: 'apr', label: 'APR' },
];

export const DEFAULT_RATE_TYPE: RateTypeOption['key'] = 'interestRate';

export const TERM_UNIT_OPTIONS: readonly TermUnitOption[] = [
  { key: 'months', label: 'Months', monthsPerUnit: 1 },
  { key: 'years', label: 'Years', monthsPerUnit: MONTHS_PER_YEAR },
];

export const DEFAULT_TERM_UNIT: TermUnitOption['key'] = 'years';

export const FEE_TREATMENT_OPTIONS: readonly FeeTreatmentOption[] = [
  { key: 'deducted', label: 'Deducted' },
  { key: 'paid', label: 'Paid' },
];

export const DEFAULT_FEE_TREATMENT: FeeTreatmentOption['key'] = 'deducted';

// A closed set with a default, so a key that misses is a coding error rather than user input.
export const getTermUnit = (key: TermUnit): TermUnitOption => {
  const option = TERM_UNIT_OPTIONS.find(unit => unit.key === key);

  if (!option) throw new Error(`Unsupported term unit: ${key}`);

  return option;
};

/** Marks every string Legal has yet to supply, so it cannot pass for approved copy in a QA screenshot. */
const PLACEHOLDER = '[PLACEHOLDER COPY - PENDING LEGAL]';

export const DISCLAIMER = `${PLACEHOLDER} This calculator is for educational purposes only.`;

export const PLACEHOLDER_BODY = `${PLACEHOLDER} The loan calculator is coming soon.`;

export const RATE_TYPE_HINTS: Readonly<Record<RateType, string>> = {
  interestRate: 'The percentage a lender charges every year to borrow the money, before fees.',
  apr: "APR stands for Annual Percentage Rate. It's the total yearly cost of your loan, including interest plus fees, shown as one percentage annually.",
};

export const EXTRA_PAYMENT_FIELD: CalculatorField = {
  label: 'Extra payment',
  hint: 'An additional amount you pay toward your loan balance each month or annually that can help you pay off the loan faster and with less interest.',
};

export const INCLUDE_FEES_FIELD: CalculatorField = {
  label: 'Include fees',
  hint: "Use this toggle to add an origination fee and/or insurance cost to your loan. These are optional and won't be included unless you select this.",
};

/** The teaser models this share of the scheduled payment when the user has not entered an extra one. */
export const DEFAULT_EXTRA_PAYMENT_RATE = 0.05;

/**
 * A 50-year term is past anything the field accepts, so reaching this means the balance stopped
 * falling and the schedule would never terminate.
 */
export const MAX_MONTHS = 600;
